import { Component } from 'react'
import { SUPPORT_EMAIL } from 'constants/email'
import ExternalLayout from 'components/features/external/ExternalLayout/ExternalLayout'
import { ExternalPageHeading } from 'components/atoms/Text'
import Anchor from 'components/atoms/navigation/Anchor'
import { NavigationLink } from 'components/atoms/navigation/NavigationLink'
import TableOfContents from 'components/atoms/TableOfContents'
import { smoothScrollInfoView } from 'helpers/styleHelpers'
import * as styles from 'styles/pages/ContentsBased.css'
import * as faqStyles from 'styles/pages/FAQ.css'
import { APP_STORE_URL, PLAY_MARKET_URL } from 'constants/media'
import { EmailLink } from 'components/features/pfm/EmailLink/EmailLink'
import { MIN_REDEMPTION_AMOUNT, MAX_REDEMPTION_CONFIRMATION_TERM } from 'constants/bounty'
import { AnalyticsClick } from 'components/features/pfm/AnalyticsClick/AnalyticsClick'
import { PageMeta } from 'components/features/pfm/PageMeta/PageMeta'

const supportEmailChild = (
  <EmailLink email={ SUPPORT_EMAIL } />
)

const privacyLinkChild = (
  <NavigationLink to='privacy'>Privacy Policy</NavigationLink>
)

const securityLinkChild = (
  <NavigationLink to='security'>Security Practices</NavigationLink>
)

const termsLinkChild = (
  <NavigationLink to='terms'>Terms</NavigationLink>
)

const questions = [
  {
    key: 'comparisons',
    question: 'How do the Status peer comparisons work?',
    answer: (
      <p>
        Status enables members to compare their finances with their peers, the national average, and more than two million custom groups
        you can create! These comparisons are powered by a unique and anonymous panel that Status created in partnership with data companies.
        This ensures that the comparisons are unbiased and accurate.
      </p>
    ),
  },
  {
    key: 'money',
    question: 'How does Status make money?',
    answer: (
      <p>
        Status makes money by matching members with personalized offers for relevant products and services. For example, if Status determines
        that the interest rate on your savings account is lower than the interest rates your peers are earning, it may show you an offer from a
        bank offering a more competitive rate. Status also offers a Premium membership option that unlocks access to financial planning tools and
        advisors. Revenue from offers and Premium memberships enables us to keep most of the app’s features free for all members.
      </p>
    ),
  },
  {
    key: 'free',
    question: 'Is Status really free?',
    answer: (
      <p>
        Yes. Status makes money by matching members with personalized offers for relevant products and services. It also offers a Premium
        membership option that unlocks access to financial planning tools and advisors. Your personal information is never shared with advertisers
        or sold to anyone. Offers and Premium memberships enable us to keep most of the app’s features free for all members.
      </p>
    ),
  },
  {
    key: 'outsideUS',
    question: 'Is Status available outside of the United States?',
    answer: (
      <p>
        No. Status is only available in the United States right now.
      </p>
    ),
  },
  {
    key: 'protect',
    question: 'How does Status protect my information?',
    answer: (
      <p>
        Status safeguards your information using state-of-the-art technology and has been independently rated by a leading security agency to be more secure than most US bank websites.
        It encrypts your information using 256-bit encryption and all its servers are monitored 24/7. For more information, please see our { securityLinkChild }.
      </p>
    ),
  },
  {
    key: 'thirdParties',
    question: 'Does Status share my personal information with any third-parties?',
    answer: (
      <p>
        Status will never sell your personal information to anyone. It may only share information with partners that enable it to provide services to you.
        For example, Status may share some information with TransUnion® to provide you with your free credit report. For more information about our privacy practices please see our { privacyLinkChild }.
      </p>
    ),
  },
  {
    key: 'mobileVersion',
    question: 'Can I access my Status account from my mobile phone?',
    answer: (
      <p>
        Yes! You can download the <a href={ APP_STORE_URL }>Status Money iOS App</a> from the App Store or the <a href={ PLAY_MARKET_URL }>Status Money Android App</a> from the Play Store.
        You can also access the Status website from your mobile phone's web browser.
      </p>
    ),
  },
  {
    key: 'haveToLinkCreditFile',
    question: 'Do I have to link my credit file to use Status?',
    answer: (
      <p>
        No, linking your credit file is optional. That said, we do recommend linking your credit file because Status uses information from your credit report to accurately match you with your peers, analyze your interest rates, and provide you with free credit report monitoring. Your information will never be sold to anyone.
      </p>
    ),
  },
  {
    key: 'haveToLinkFinancialAccounts',
    question: 'Do I have to link my financial accounts to use Status?',
    answer: (
      <p>
        We recommend linking your financial accounts to Status so you can always have a comprehensive view of your net worth, assets, debts, spending, and income.
        Status will also track the interest rates on your linked accounts and alert you if your rates are not competitive compared with your peers.
        You may also add accounts manually, but transactions can not be manually imported right now.
      </p>
    ),
  },
  {
    key: 'institutionNotListed',
    question: 'My financial institution is not listed, how can I request Status to add it?',
    answer: (
      <p>
        If your institution is not available on Status, you can request to have it added by emailing us at: { supportEmailChild }.
        In the meantime, you can manually add your account to ensure that your net worth, assets, and debts are accurate.
      </p>
    ),
  },
  {
    key: 'manualAccounts',
    question: 'Can I manually add financial accounts?',
    answer: (
      <p>
        Status securely connects with more than 12,000 financial institutions.
        If your institution is not available, you can manually add your accounts to ensure that your net worth, assets, and debts are accurate.
        You can request to have your institution added to Status by emailing us at: { supportEmailChild }.
      </p>
    ),
  },
  {
    key: 'linkingError',
    question: 'Why am I experiencing an error while trying to link my financial accounts?',
    answer: (
      <p>
        The most common linking error members experience is caused by incorrect credentials.
        If you’re certain that your credentials are correct, please ensure that you’re linking the correct financial institution (since some institutions have multiple websites which are individually listed on Status).
        If you are still experiencing linking errors, please feel free to contact us at: { supportEmailChild }.
      </p>
    ),
  },
  {
    key: 'deleteAccount',
    question: 'Can I delete an account that I linked to Status?',
    answer: (
      <p>
        Yes, you can delete any individual account from your Settings page.
        Deleting an account will remove all its historical balances and transactions.
        Alternatively, you can unlink an account to preserve its historical data, but stop future data refreshes – this option is useful for accounts that you have closed.
      </p>
    ),
  },
  {
    key: 'retrieveHistoricalTransactions',
    question: 'Why didn’t Status retrieve all my historical transactions?',
    answer: (
      <p>
        Status attempts to retrieve all historical transactions for linked accounts to provide you with accurate historical comparisons and to calculate your future spending and income projections.
        In most cases, Status can retrieve 6 months or more of historical transactions, but some banks restrict their transaction history to 3 months or less.
        Rest assured, if your account is successfully linked, all future transactions will be automatically synced.
      </p>
    ),
  },
  {
    key: 'changeTransactionCategory',
    question: 'Can I change a transaction’s category?',
    answer: (
      <p>
        Yes, you can update any transaction’s category by clicking on its category label. Please note that a transaction’s category can’t be edited
        while it is listed as pending by your financial institution. Pending transactions are usually settled within a few days.
      </p>
    ),
  },
  {
    key: 'excludeTransaction',
    question: 'Can I exclude a transaction from my spending and income?',
    answer: (
      <p>
        Yes, you can exclude any transaction from your spending and income by changing its category to “Transfer”.
        Transfers are excluded from all balances and not shown in charts and comparisons.
      </p>
    ),
  },
  {
    key: 'setTrackers',
    question: 'How can I create a budget?',
    answer: (
      <p>
        You can set trackers for any spending and income category to easily manage your budget. To set a tracker for a particular
        category, click on the category in the Breakdown by Category section of your Spending and Income pages. Once set, the tracker will
        appear on your Dashboard and show you if your budget is on-track or not.
      </p>
    ),
  },
  {
    key: 'historicalPropertyValues',
    question: 'Why are my historical Home and Car values different from the values I entered?',
    answer: (
      <p>
        The Home and Car values that you enter when you link these assets is the current month’s value.
        Since the values of these assets change over time, Status automatically estimates their historical and future values
        by adjusting for the monthly depreciation or appreciation based on the Home’s location and the Car’s make and model.
        You can edit a Home or Car's current value at any time from the Settings page.
      </p>
    ),
  },
  {
    key: 'couples',
    question: 'Can couples use Status to manage their household finances?',
    answer: (
      <p>
        Yes, couples can use Status to manage their finances by creating individual accounts and linking their joint financial accounts to both of their Status accounts.
        This enables them to track their balances and transactions together, while also giving each of them the ability to monitor their individual credit report.
      </p>
    ),
  },
  {
    key: 'earnRewards',
    question: 'How can I earn rewards?',
    answer: (
      <p>
        You are automatically enrolled in our rewards program when you create your account! You will earn cash rewards for joining, linking your
        first bank account, linking your credit report, engaging with opportunities throughout the app, and referring friends. To find new ways to earn, simply
        look for the golden reward badges. Tapping these badges shows you what you need to do to earn the reward.
      </p>
    ),
  },
  {
    key: 'rewardBalance',
    question: 'How long does it take for my reward to be added to my balance?',
    answer: (
      <p>
        Rewards earned for joining, setting up your account, and referring friends are instantly added to your balance. Rewards earned for
        engaging with opportunities may take up to 60 days to be added to your balance. You can track all your pending and earned rewards on
        the Rewards page and we will automatically notify you via email when a reward is confirmed or added to your balance.
      </p>
    ),
  },
  {
    key: 'trackRewards',
    question: 'How can I track my pending rewards?',
    answer: (
      <p>
        You can see all your pending and earned rewards on the Rewards page.
        When you take an action that qualifies for a reward, we automatically begin tracking it and notify you when the reward is confirmed or added to your balance.
        For some rewards, it may take up to { MAX_REDEMPTION_CONFIRMATION_TERM } days for the reward to be added to your balance.
      </p>
    ),
  },
  {
    key: 'whereAreRewards',
    question: 'Why did my reward not get added to my balance?',
    answer: (
      <p>
        Rewards earned for engaging with opportunities are added to your balance after we verify that the reward’s requirements were fulfilled.
        This happens automatically, but it may take up to { MAX_REDEMPTION_CONFIRMATION_TERM } days for the reward to be added to your balance.
        If you believe that you earned a reward that was not added to your balance after { MAX_REDEMPTION_CONFIRMATION_TERM } days, please contact us at: { supportEmailChild }.
      </p>
    ),
  },
  {
    key: 'expiredRewards',
    question: 'Do my rewards expire?',
    answer: (
      <p>
        No, earned rewards do not expire.
        But your rewards are forfeited when your Status account is deleted for any reason.
        Your rewards may also be forfeited if you attempt to earn rewards in a fraudulent way or violate our { termsLinkChild }.
      </p>
    ),
  },


  {
    key: 'rewardsLimits',
    question: 'Is there a limit to the rewards I can earn?',
    answer: (
      <p>
        Nope! There’s no limit to the amount of rewards you can earn.
      </p>
    ),
  },
  {
    key: 'howCryptoWorks',
    question: 'How do crypto rewards work?',
    answer: (
      <p>
        Status Money can automatically invest your cash rewards in crypto, so they can potentially increase in value over time! You can manually initiate
        crypto purchases or activate the auto-investing option to automatically have your cash rewards invested in crypto on an ongoing basis. You need a
        cash rewards balance of at least ${ MIN_REDEMPTION_AMOUNT } to initiate your first crypto purchase.
      </p>
    ),
  },
  {
    key: 'aboutCrypto',
    question: 'Do I need to know anything about crypto to participate?',
    answer: (
      <p>
        Nope! We designed our rewards program to make it incredibly simple for anyone to benefit from investing in crypto. We handle all the buying,
        selling, and taxes – and you get the investment returns.
      </p>
    ),
  },
  {
    key: 'supportedCryptos',
    question: 'What cryptos can I invest in?',
    answer: (
      <p>
        You can have your rewards invested in any crypto, as long as it's Bitcoin.
      </p>
    ),
  },
  {
    key: 'addMoney',
    question: 'Can I add money to my rewards balance?',
    answer: (
      <p>
        You can earn rewards to add money to your rewards balance by creating a Status account, linking your bank accounts, linking
        your credit report, engaging with opportunities, and referring friends. There’s no limit to how much you can earn.
        You can’t transfer money into your rewards balance.
      </p>
    ),
  },
  {
    key: 'trackPerformance',
    question: 'How do I track the performance of my crypto?',
    answer: (
      <p>
        You can track the value and performance of your crypto rewards at any time by logging into your Status account and
        going to the Rewards screen.
      </p>
    ),
  },
  {
    key: 'sellCrypto',
    question: 'When can I sell my crypto?',
    answer: (
      <p>
        There’s no minimum holding period for crypto investments, so you can sell them at any time.
      </p>
    ),
  },
  {
    key: 'insured',
    question: 'Are my crypto investments insured?',
    answer: (
      <p>
        No. Crypto investments may lose value and are not FDIC or SIPC insured – this is why we made it possible to
        speculate on crypto using your rewards. Keep your rent money and savings where they belong!
      </p>
    ),
  },
  {
    key: 'cryptoFees',
    question: 'Are there any fees for crypto rewards?',
    answer: (
      <p>
        There are no fees to have your cash rewards invested in crypto. You can submit a sell order at any time to have
        some or all your crypto rewards converted back into cash. We will add the sale proceeds to your cash rewards balance minus any
        estimated taxes and a transaction fee when the sale is completed.
      </p>
    ),
  },

  {
    key: 'redeemRewards',
    question: 'How can I redeem my rewards?',
    answer: (
      <p>
        You can redeem your rewards when your cash rewards balance reaches or exceeds ${ MIN_REDEMPTION_AMOUNT }. Once eligible, the redemption option
        will appear on your Rewards page. Redemptions occur via a free ACH transfer to a bank account of your choosing.
      </p>
    ),
  },
] as const

export type QuestionKey = (typeof questions)[any]['key']

type RefNodeKey =
  | QuestionKey
  | 'top'
  | 'heading'
class FAQ extends Component {
  private refNodes: Partial<Record<RefNodeKey, HTMLElement | null>> = {}

  render () {
    const tocTitle = <a onClick={ () => this.scrollToSection('heading') }>Frequently Asked Questions</a>
    const toTopLink = <a className={ styles.toTopLink } onClick={ () => this.scrollToSection('top') }>Back To Top</a>

    return (
      <Anchor placement='faq'>
        <PageMeta
          title='FAQ | Status Money'
          description='The Status Money team is here to help. Here are the answers to the most common questions.'
        />
        <span ref={ (el) => { this.refNodes.top = el } } />
        <ExternalLayout
          type='common'
        >
          <article className={ styles.content }>
            <div className={ faqStyles.tableOfContentsWrapper }>
              <TableOfContents title={ tocTitle }>
                {
                  questions.map(({ key, question }) => (
                    <AnalyticsClick
                      category='page_controls-faq'
                      action='click-question_nav'
                      label={ key }
                      key={ key }
                    >
                      <a onClick={ () => this.scrollToSection(key) }>
                        { question }
                      </a>
                    </AnalyticsClick>
                  ))
                }
              </TableOfContents>
            </div>
            <div className={ styles.heading }>
              <span className={ styles.sectionAnchor } ref={ (el) => this.refNodes.heading = el } />
              <ExternalPageHeading>FAQ</ExternalPageHeading>
            </div>
            <section className={ faqStyles.content }>
              {
                questions.map(({ key, question, answer }) => (
                  <div key={ key } className={ styles.paragraph }>
                    <span className={ styles.sectionAnchor } ref={ (el) => { this.refNodes[key] = el } } />
                    <h3 className={ styles.paragraphTitle }>{ question }</h3>
                    { toTopLink }
                    { answer }
                  </div>
                ))
              }
            </section>

            <div className={ faqStyles.emailSection }>
              Still have questions?<br />
              Please email us at: { supportEmailChild }
            </div>
          </article>
        </ExternalLayout>
      </Anchor>
    )
  }

  private scrollToSection = (section: RefNodeKey) => {
    const node = this.refNodes[section]
    if (node) {
      smoothScrollInfoView(node, 'start')
    }
  }
}

export default FAQ
